import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function AdminDogs() {
  const [pendingDogs, setPendingDogs] = useState([]);
  const [deniedDogs, setDeniedDogs] = useState([]);
  const [message, setMessage] = useState('');
  const [dogImages, setDogImages] = useState({});
  const [denyReasons, setDenyReasons] = useState({});
  const [replies, setReplies] = useState({});
  const [loading, setLoading] = useState(true);
  const adminId = localStorage.getItem('playerId');

  useEffect(() => {
    if (!adminId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const pendingRes = await fetch(`${API_BASE_URL}/admin-pending?adminId=${adminId}`);
        if (!pendingRes.ok) throw new Error(await pendingRes.text());
        const pendingData = await pendingRes.json();
        setPendingDogs(pendingData.dogs || []);

        const deniedRes = await fetch(`${API_BASE_URL}/admin-denied?adminId=${adminId}`);
        if (!deniedRes.ok) throw new Error(await deniedRes.text());
        setDeniedDogs(await deniedRes.json() || []);
      } catch (error) {
        setMessage('Error loading dogs: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adminId]);

  const approveDog = async (dogId) => {
    try {
      const dog = pendingDogs.find(d => d._id === dogId);
      if (dog.playerInfo._id === adminId) {
        setMessage('Admins cannot approve their own dogs.');
        return;
      }
      const artUrl = dogImages[dogId] || dog.stashLink;
      const res = await fetch(`${API_BASE_URL}/admin-approve-dog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dogId, artUrl })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message);
      setPendingDogs(pendingDogs.filter(d => d._id !== dogId));
      setDogImages(prev => { const { [dogId]: _, ...rest } = prev; return rest; });
    } catch (error) {
      setMessage('Error approving dog: ' + error.message);
    }
  };

  const denyDog = async (dogId) => {
    try {
      const denyReason = denyReasons[dogId] || 'No reason provided';
      const res = await fetch(`${API_BASE_URL}/admin-deny-dog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dogId, denyReason, adminId })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message);
      setPendingDogs(pendingDogs.filter(d => d._id !== dogId));
      setDenyReasons(prev => { const { [dogId]: _, ...rest } = prev; return rest; });
    } catch (error) {
      setMessage('Error denying dog: ' + error.message);
    }
  };

  const clearDeniedDog = async (dogId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dog/${dogId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(await res.text());
      setDeniedDogs(deniedDogs.filter(d => d._id !== dogId));
      setMessage('Dog cleared!');
    } catch (error) {
      setMessage('Error clearing dog: ' + error.message);
    }
  };

  const sendReply = async (dogId, section) => {
    try {
      const text = replies[dogId] || '';
      if (!text) return;
      const res = await fetch(`${API_BASE_URL}/dog-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dogId, senderId: adminId, senderRole: 'admin', text })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message);
      const newMessage = { senderId: adminId, senderRole: 'admin', text, timestamp: new Date(), unread: true };
      if (section === 'pending') {
        setPendingDogs(pendingDogs.map(d => d._id === dogId ? { ...d, messages: [...d.messages, newMessage] } : d));
      } else {
        setDeniedDogs(deniedDogs.map(d => d._id === dogId ? { ...d, messages: [...d.messages, newMessage] } : d));
      }
      setReplies(prev => ({ ...prev, [dogId]: '' }));
    } catch (error) {
      setMessage('Error sending reply: ' + error.message);
    }
  };

  const markMessageRead = async (dogId, msgIdx, section) => {
    try {
      const dogArray = section === 'pending' ? pendingDogs : deniedDogs;
      const dog = dogArray.find(d => d._id === dogId);
      const updatedMessages = dog.messages.map((m, i) => i === msgIdx ? { ...m, unread: false } : m);
      const res = await fetch(`${API_BASE_URL}/dog/${dog.dogId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dog, messages: updatedMessages })
      });
      if (!res.ok) throw new Error(await res.text());
      if (section === 'pending') {
        setPendingDogs(pendingDogs.map(d => d._id === dogId ? { ...d, messages: updatedMessages } : d));
      } else {
        setDeniedDogs(deniedDogs.map(d => d._id === dogId ? { ...d, messages: updatedMessages } : d));
      }
    } catch (error) {
      setMessage('Error marking message as read: ' + error.message);
    }
  };

  if (!adminId) return <p style={{ textAlign: 'center', color: '#333' }}>Please log in as an admin.</p>;
  if (loading) return <p style={{ textAlign: 'center', color: '#333' }}>Loading dog approvals...</p>;

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1 style={{ textAlign: 'center', color: '#333', marginBottom: '20px' }}>Dog Approvals</h1>
      {message && <p style={{ textAlign: 'center', color: message.includes('Error') ? '#FF4444' : '#4CAF50' }}>{message}</p>}

      <h2 style={{ color: '#4CAF50', marginBottom: '15px' }}>Pending Dogs</h2>
      {pendingDogs.length > 0 ? (
        pendingDogs.map(dog => (
          <div key={dog._id} style={{ marginBottom: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
            <p><strong>{dog.registeredName}</strong> ({dog.breed}) - ID: {dog.dogId || 'Pending'}</p>
            <p>Call Name: {dog.callName || 'N/A'} | Gender: {dog.gender}</p>
            <p>Owner: {dog.playerInfo?.username || 'Unknown'}</p>
            <p>Kennel: {dog.kennelInfo?.kennelName || 'N/A'}</p>
            <p>Sire: {dog.sireInfo?.dogId ? <Link to={`/dog/${dog.sireInfo.dogId}`}>{dog.sireInfo.registeredName}</Link> : dog.sireId || 'N/A'}</p>
            <p>Dam: {dog.damInfo?.dogId ? <Link to={`/dog/${dog.damInfo.dogId}`}>{dog.damInfo.registeredName}</Link> : dog.damId || 'N/A'}</p>
            <p>Submitted Artwork: <a href={dog.stashLink} target="_blank" rel="noopener noreferrer" style={{ color: '#4CAF50' }}>{dog.stashLink}</a></p>
            <p>Using Edit Item: {dog.usingItems ? 'Yes' : 'No'}{dog.usingItems && ` | Edits Declared: ${dog.itemsUsed || 'N/A'}`}</p>
            <label>Image URL: <input value={dogImages[dog._id] || ''} onChange={e => setDogImages({ ...dogImages, [dog._id]: e.target.value })} style={{ width: '100%', padding: '5px', marginTop: '5px' }} /></label>
            <div style={{ marginTop: '10px' }}>
              <button onClick={() => approveDog(dog._id)} style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginRight: '10px' }}>Approve</button>
              <button onClick={() => denyDog(dog._id)} style={{ background: '#FF69B4', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginRight: '10px' }}>Deny</button>
              <input value={denyReasons[dog._id] || ''} onChange={e => setDenyReasons({ ...denyReasons, [dog._id]: e.target.value })} placeholder="Reason for denial" style={{ padding: '5px', width: '200px' }} />
            </div>
            <h3 style={{ color: '#4CAF50', marginTop: '10px' }}>Messages</h3>
            {dog.messages?.length > 0 ? (
              <ul style={{ paddingLeft: '20px' }}>
                {dog.messages.map((msg, idx) => (
                  <li key={idx} style={{ color: msg.unread && msg.senderRole === 'player' ? '#FF4444' : '#333' }}>
                    {msg.senderRole}: {msg.text} ({new Date(msg.timestamp).toLocaleString()})
                    {msg.unread && msg.senderRole === 'player' && (
                      <button onClick={() => markMessageRead(dog._id, idx, 'pending')} style={{ marginLeft: '10px', background: '#4CAF50', color: 'white', border: 'none', padding: '2px 5px', borderRadius: '3px' }}>Mark Read</button>
                    )}
                  </li>
                ))}
              </ul>
            ) : <p>No messages.</p>}
            <textarea value={replies[dog._id] || ''} onChange={e => setReplies({ ...replies, [dog._id]: e.target.value })} placeholder="Reply..." style={{ width: '100%', marginTop: '10px', padding: '5px' }} />
            <button onClick={() => sendReply(dog._id, 'pending')} style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginTop: '5px' }}>Send Reply</button>
          </div>
        ))
      ) : <p style={{ textAlign: 'center', color: '#888' }}>No pending dogs.</p>}

      <h2 style={{ color: '#4CAF50', marginBottom: '15px', marginTop: '20px' }}>Denied Dogs</h2>
      {deniedDogs.length > 0 ? (
        deniedDogs.map(dog => (
          <div key={dog._id} style={{ marginBottom: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
            <p><strong>{dog.registeredName}</strong> ({dog.breed}) - Denied</p>
            <p>Call Name: {dog.callName || 'N/A'} | Gender: {dog.gender}</p>
            <p>Owner: {dog.playerInfo?.username || 'Unknown'}</p>
            <p>Kennel: {dog.kennelInfo?.kennelName || 'N/A'}</p>
            <p>Sire: {dog.sireInfo?.dogId ? <Link to={`/dog/${dog.sireInfo.dogId}`}>{dog.sireInfo.registeredName}</Link> : dog.sireId || 'N/A'}</p>
            <p>Dam: {dog.damInfo?.dogId ? <Link to={`/dog/${dog.damInfo.dogId}`}>{dog.damInfo.registeredName}</Link> : dog.damId || 'N/A'}</p>
            <p>Submitted Artwork: <a href={dog.stashLink} target="_blank" rel="noopener noreferrer" style={{ color: '#4CAF50' }}>{dog.stashLink}</a></p>
            <p>Reason: {dog.denyReason || 'N/A'}</p>
            <p>Using Edit Item: {dog.usingItems ? 'Yes' : 'No'}{dog.usingItems && ` | Edits Declared: ${dog.itemsUsed || 'N/A'}`}</p>
            <button onClick={() => clearDeniedDog(dog._id)} style={{ background: '#FF69B4', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginTop: '10px' }}>Clear</button>
            <h3 style={{ color: '#4CAF50', marginTop: '10px' }}>Messages</h3>
            {dog.messages?.length > 0 ? (
              <ul style={{ paddingLeft: '20px' }}>
                {dog.messages.map((msg, idx) => (
                  <li key={idx} style={{ color: msg.unread && msg.senderRole === 'player' ? '#FF4444' : '#333' }}>
                    {msg.senderRole}: {msg.text} ({new Date(msg.timestamp).toLocaleString()})
                    {msg.unread && msg.senderRole === 'player' && (
                      <button onClick={() => markMessageRead(dog._id, idx, 'denied')} style={{ marginLeft: '10px', background: '#4CAF50', color: 'white', border: 'none', padding: '2px 5px', borderRadius: '3px' }}>Mark Read</button>
                    )}
                  </li>
                ))}
              </ul>
            ) : <p>No messages.</p>}
            <textarea value={replies[dog._id] || ''} onChange={e => setReplies({ ...replies, [dog._id]: e.target.value })} placeholder="Reply..." style={{ width: '100%', marginTop: '10px', padding: '5px' }} />
            <button onClick={() => sendReply(dog._id, 'denied')} style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginTop: '5px' }}>Send Reply</button>
          </div>
        ))
      ) : <p style={{ textAlign: 'center', color: '#888' }}>No denied dogs.</p>}
    </div>
  );
}

export default AdminDogs;